import React, { useEffect, useState } from "react";
import {
  addAllDashboard,
  allTreeData,
  getDashboardOwnersList,
  getDashboardAdmin,
  setDashboardUnPublishing,
  updateDashboardOwners,
  setDashboardPendingPublishing,
  getDashboardRedirectTargets,
  getDashboardRedirectTypes,
  insertDashboardRedirect,
} from "../../../utils/api";
import { getGlobalState } from "../../../utils/globalState";

const DashboardAdmin = ({ expandedSections, toggleSection, runAs, theme }) => {
  // receive props theme
  const myUserId = getGlobalState().myUserId;
  const runner = runAs === "admin" ? 2 : myUserId;
  const [publishingTargets, setPublishingTargets] = useState([]);
  const [publishingFilters, setPublishingFilters] = useState({
    name: "",
    owner: "",
    instance: "",
    status: "",
    daysSinceViewed: "",
  });
  const [checkboxes, setCheckboxes] = useState({
    checkbox1: false,
    checkbox2: false,
    checkbox3: false,
  });
  const allCheckboxesChecked = Object.values(checkboxes).every(Boolean);
  const [publishingCurrentPage, setPublishingCurrentPage] = useState(1);
  const [showRedirectPopup, setShowRedirectPopup] = useState(false);
  const [loadingPublishing, setLoadingPublishing] = useState(true);
  const [showPublishPopup, setShowPublishPopup] = useState(false);
  const [selectedDashboardId, setSelectedDashboardId] = useState(null);
  const [selectedDashboardType, setSelectedDashboardType] = useState(null);
  const [selectedDashboardName, setSelectedDashboardName] = useState(null);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const publishingItemsPerPage = 15;
  const [ownerSearch, setOwnerSearch] = useState("");
  const [nonOwnerSearch, setNonOwnerSearch] = useState("");
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [selectedNonOwner, setSelectedNonOwner] = useState(null);
  const [showOwnersPopup, setShowOwnersPopup] = useState(false);
  const [showCheckboxPopup, setShowCheckboxPopup] = useState(false);
  const publishingUniqueInstances = [
    ...new Set(publishingTargets.map((d) => d.instance_name)),
  ];
  const publishingUniqueStatuses = [
    ...new Set(publishingTargets.map((d) => d.status)),
  ];
  const [ownersList, setOwnersList] = useState([]);
  const [nonOwnersList, setNonOwnersList] = useState([]);
  const [ownerDashboardId, setOwnerDashboardId] = useState([]);
  const [redirectTypes, setRedirectTypes] = useState([]);
  const [selectedRedirectType, setSelectedRedirectType] = useState("");
  const [redirectTargets, setRedirectTargets] = useState([]);
  const [selectedRedirectTarget, setSelectedRedirectTarget] = useState("");
  const [redirectTargetFilter, setRedirectTargetFilter] = useState("");

  const fetchPublishingTargets = async () => {
    try {
      setLoadingPublishing(true);
      const publishingTargetsData = await getDashboardAdmin(runner);
      setPublishingTargets(publishingTargetsData);
      setLoadingPublishing(false);
    } catch (error) {
      setLoadingPublishing(false);
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchPublishingTargets();
    };
    fetchData();
  }, [myUserId]);

  const formatDate = () => {
    const date = new Date();
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("default", { month: "short" });
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  };

  const handlePublishDashboard = async () => {
    if (allCheckboxesChecked) {
      try {
        await setDashboardPendingPublishing(selectedDashboardId);
        alert(
          "The Dashboard status has been set to pending, and will be reviewed by the admin team."
        );
        await fetchPublishingTargets();
      } catch (error) {
        console.error("Error publishing dashboard:", error);
        alert("Error publishing dashboard");
      } finally {
        setShowCheckboxPopup(false);
      }
    }
  };

  const handleCloseCheckboxPopup = () => {
    setShowCheckboxPopup(false);
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setCheckboxes((prevCheckboxes) => ({
      ...prevCheckboxes,
      [name]: checked,
    }));
  };

  const handleDashboardRedirectClick = async (dashboardId, instance, name) => {
    setShowRedirectPopup(true);
    const redirectResponse = await getDashboardRedirectTypes();
    setRedirectTypes(redirectResponse);
    setSelectedRedirectType("");
    setSelectedDashboardId(dashboardId);
    setSelectedDashboardType(instance);
    setSelectedDashboardName(name);

    // Fetch redirect targets
    const targets = await getDashboardRedirectTargets();
    setRedirectTargets(targets);
    setSelectedRedirectTarget("");
  };

  const handleRedirectDashboard = async () => {
    await insertDashboardRedirect(
      selectedDashboardId,
      selectedRedirectTarget,
      selectedRedirectType
    );
    setShowRedirectPopup(false);
  };

  // Filtering function for publishing targets
  const applyPublishingFilters = (data) => {
    return data.filter((item) => {
      return (
        (publishingFilters.name === "" ||
          item.dashboard_name
            .toLowerCase()
            .includes(publishingFilters.name.toLowerCase())) &&
        (publishingFilters.owner === "" ||
          (item.owner &&
            item.owner
              .toLowerCase()
              .includes(publishingFilters.owner.toLowerCase()))) &&
        (publishingFilters.instance === "" ||
          item.instance_name === publishingFilters.instance) &&
        (publishingFilters.status === "" ||
          item.status === publishingFilters.status) &&
        (publishingFilters.daysSinceViewed === "" ||
          item.days_since_viewed >= parseInt(publishingFilters.daysSinceViewed))
      );
    });
  };

  const filteredPublishingTargets = applyPublishingFilters(publishingTargets);

  const pagedFilteredPublishingTargets = filteredPublishingTargets.slice(
    (publishingCurrentPage - 1) * publishingItemsPerPage,
    publishingCurrentPage * publishingItemsPerPage
  );

  const handlePublishClick = async (dashboardId) => {
    try {
      const allTreeDataResponse = await allTreeData();
      const foldersOnly = filterFolders(allTreeDataResponse);
      setTreeData(foldersOnly);
      setShowPublishPopup(true);
      setSelectedDashboardId(dashboardId);
    } catch (error) {
      console.error("Error fetching allTreeData:", error);
    }
  };

  const handleOwnerPublishClick = (dashboardId) => {
    setShowCheckboxPopup(true);
    setSelectedDashboardId(dashboardId);
  };

  const filteredOwners = ownersList.filter(
    (user) =>
      user.user_full_name &&
      user.user_full_name.toLowerCase().includes(ownerSearch.toLowerCase())
  );
  const filteredNonOwners = nonOwnersList.filter(
    (user) =>
      user.user_full_name &&
      user.user_full_name.toLowerCase().includes(nonOwnerSearch.toLowerCase())
  );

  const handleAddRemoveOwners = async (dashboardId) => {
    setOwnerDashboardId(dashboardId);
    const response = await getDashboardOwnersList(dashboardId);
    setOwnersList(response.filter((user) => user.is_owner === 1));
    setNonOwnersList(response.filter((user) => user.is_owner !== 1));
    setShowOwnersPopup(true);
  };

  const handleUnpublishOrCancel = async (dashboardId) => {
    setSelectedDashboardId(dashboardId);
    // console.log('Selected Dashboard ID:', dashboardId);
    setShowConfirmationPopup(true);
    await fetchPublishingTargets();
  };

  const handleUpdateOwners = async () => {
    setShowOwnersPopup(false);
    const ownerUserIds = ownersList
      .filter((user) => user.full_owner === 0)
      .map((user) => user.user_id);
    await updateDashboardOwners(ownerDashboardId, ownerUserIds);
  };

  const filterFolders = (data) => {
    return data
      .map((node) => {
        if (node.type === "folder") {
          return {
            ...node,
            children: filterFolders(node.children),
          };
        }
        return null;
      })
      .filter((node) => node !== null);
  };

  // Handler for publishing filters
  const handlePublishingFilterChange = (e) => {
    const { name, value } = e.target;
    setPublishingFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
    setPublishingCurrentPage(1); // Reset to first page on filter change
  };
  const handleConfirmation = async (confirmed) => {
    if (confirmed) {
      await setDashboardUnPublishing(selectedDashboardId);
      await fetchPublishingTargets();
    }
    setShowConfirmationPopup(false);
  };

  // Pagination for publishing targets (same as renderPagination, but for publishing)
  const renderPublishingPagination = () => {
    const totalPages = Math.ceil(
      filteredPublishingTargets.length / publishingItemsPerPage
    );
    const paginationLinks = [];

    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) {
        paginationLinks.push(
          <button
            key={i}
            onClick={() => setPublishingCurrentPage(i)}
            // className={publishingCurrentPage === i ? "active" : ""}
            className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
              publishingCurrentPage === i
                ? "z-10 focus:z-20"
                : " text-gray-500 hover:bg-gray-50 focus:z-20"
            } border border-gray-300 mx-1 rounded`}
             style={publishingCurrentPage === i ? { backgroundColor: theme.leftNavBackgroundColor, color: "#fff" } : {backgroundColor: "#fff"}}
          >
            {i}
          </button>
        );
      }
    } else {
      paginationLinks.push(
        <button
          key={1}
          onClick={() => setPublishingCurrentPage(1)}
        //   className={publishingCurrentPage === 1 ? "active" : ""}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
          publishingCurrentPage === 1
            ? 'z-10 bg-blue-600 text-white focus:z-20'
            : 'bg-white text-gray-500 hover:bg-gray-50 focus:z-20'
        } border border-gray-300 mx-1 rounded`}
          style={publishingCurrentPage === 1 ? { backgroundColor: theme.leftNavBackgroundColor, color: "#fff" } : {backgroundColor: "#fff"}}
        >
          1
        </button>
      );
      paginationLinks.push(
        <button
          key={2}
          onClick={() => setPublishingCurrentPage(2)}
        //   className={publishingCurrentPage === 2 ? "active" : ""}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
          publishingCurrentPage === 2
            ? 'z-10 bg-blue-600 text-white focus:z-20'
            : 'bg-white text-gray-500 hover:bg-gray-50 focus:z-20'
        } border border-gray-300 mx-1 rounded`}
          style={publishingCurrentPage === 2 ? { backgroundColor: theme.leftNavBackgroundColor, color: "#fff" } : {backgroundColor: "#fff"}}
        >
          2
        </button>
      );
      if (publishingCurrentPage > 3) {
        paginationLinks.push(<span key="ellipsis1" className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 mx-1 rounded">...</span>);
      }
      if (publishingCurrentPage > 2 && publishingCurrentPage < totalPages - 1) {
        paginationLinks.push(
          <button
            key={publishingCurrentPage}
            onClick={() => setPublishingCurrentPage(publishingCurrentPage)}
            // className="active"
            className="relative inline-flex items-center px-4 py-2 text-sm font-medium z-10 bg-blue-600 text-white focus:z-20 border border-gray-300 mx-1 rounded"
          >
            {publishingCurrentPage}
          </button>
        );
      }
      if (publishingCurrentPage < totalPages - 2) {
        paginationLinks.push(<span key="ellipsis2" className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 mx-1 rounded">...</span>);
      }
      paginationLinks.push(
        <button
          key={totalPages - 1}
          onClick={() => setPublishingCurrentPage(totalPages - 1)}
          className={publishingCurrentPage === totalPages - 1 ? "active" : ""}
        >
          {totalPages - 1}
        </button>
      );
      paginationLinks.push(
        <button
          key={totalPages}
          onClick={() => setPublishingCurrentPage(totalPages)}
        //   className={publishingCurrentPage === totalPages ? "active" : ""}
        className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
          publishingCurrentPage === totalPages - 1
            ? 'z-10  focus:z-20'
            : 'bg-white text-gray-500 hover:bg-gray-50 focus:z-20'
        } border border-gray-300 mx-1 rounded`}
          style={publishingCurrentPage === totalPages - 1 ? { backgroundColor: theme.leftNavBackgroundColor, color: "#fff" } : {backgroundColor: "#fff"}}
        >
          {totalPages}
        </button>
      );
    }

    return (
      <div className="pagination">
        {publishingCurrentPage > 1 && (
          <>
            <button className="relative inline-flex items-center px-4 py-2 mx-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" onClick={() => setPublishingCurrentPage(1)}>
              &lt;&lt;
            </button>
            <button className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              onClick={() =>
                setPublishingCurrentPage(publishingCurrentPage - 1)
              }
            >
              &lt;
            </button>
          </>
        )}
        {paginationLinks}
        {publishingCurrentPage < totalPages && (
          <>
            <button
              onClick={() =>
                setPublishingCurrentPage(publishingCurrentPage + 1)
              }
              className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              &gt;
            </button>
            <button onClick={() => setPublishingCurrentPage(totalPages)} className="relative mx-1 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
              &gt;&gt;
            </button>
          </>
        )}
      </div>
    );
  };

  const TreeNode = ({ node, onSelect, selectedNode }) => {
    const [collapsed, setCollapsed] = useState(true);

    const toggleCollapse = (e) => {
      e.stopPropagation();
      setCollapsed(!collapsed);
    };

    const handleSelect = () => {
      onSelect(node);
    };

    return (
      <div>
        <div
          onClick={handleSelect}
          style={{
            cursor: "pointer",
            backgroundColor:
              selectedNode?.id === node.id ? "#e0e0e0" : "transparent",
          }}
        >
          <span onClick={toggleCollapse} style={{ marginRight: "5px" }}>
            {collapsed ? "+" : "-"}
          </span>
          {node.label}
        </div>
        {!collapsed && node.children && (
          <div style={{ paddingLeft: "20px" }}>
            {node.children.map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                onSelect={onSelect}
                selectedNode={selectedNode}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const TreePopup = ({ treeData, onClose, selectedDashboardId }) => {
    const [selectedNode, setSelectedNode] = useState(null);

    const handleSelect = (node) => {
      setSelectedNode(node);
    };

    const handlePublish = async () => {
      try {
        await addAllDashboard(selectedNode.id, selectedDashboardId);
        const updatedTreeData = await allTreeData();
        const foldersOnly = filterFolders(updatedTreeData);
        setTreeData(foldersOnly);
        setShowPublishPopup(false);
        alert(
          "Congratulations, you have published the dashboard to the All Dashboards Tree. Please refresh the page to confirm."
        );
      } catch (error) {
        console.error("Error updating allTreeData:", error);
      }
    };
    return (
      <div className="popup-overlay">
        <div className="popup-content">
          <h3>Select the containing folder</h3>
          <div>
            {treeData.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                onSelect={handleSelect}
                selectedNode={selectedNode}
              />
            ))}
          </div>
          <button onClick={onClose}>Close</button>&nbsp;
          <button onClick={handlePublish} disabled={!selectedNode}>
            Publish
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="section">
        {/* commented cause I use new tabs view instead expand view */}
        {/* <div className="section-header" onClick={() => toggleSection('admin')}>
                    <h3>Dashboards Admin</h3>
                    <i className={`fas fa-chevron-${expandedSections.admin ? 'up' : 'down'}`}></i>
                </div> */}
        {expandedSections.admin && (
          <div className="section-content">
            {loadingPublishing ? (
              <div>Loading...</div>
            ) : (
              <>
                {/* <p>Number of dashboards: {filteredPublishingTargets.length}</p> */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    Dashboard Admin
                  </h3>
                  <p
                    className="mt-2 text-sm text-gray-600 bg-blue-50 p-3 rounded-md border-l-4 border-blue-400"
                    style={{
                      borderLeftColor: theme.leftNavBackgroundColor,
                      backgroundColor: theme.lightColor,
                    }}
                  >
                    Number of dashboards: {filteredPublishingTargets.length}
                  </p>
                </div>
                <div className="overflow-x-auto rounded-lg shadow">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="font-bold px-6 py-3 text-left text-sm text-gray-500 tracking-wider"
                        >
                          Dashboard Name
                        </th>
                        <th
                          scope="col"
                          className="font-bold px-6 py-3 text-left text-sm text-gray-500 tracking-wider"
                        >
                          Owner
                        </th>
                        <th
                          scope="col"
                          className="font-bold px-6 py-3 text-left text-sm text-gray-500 tracking-wider"
                        >
                          Instance
                        </th>
                        <th
                          scope="col"
                          className="font-bold px-6 py-3 text-left text-sm text-gray-500 tracking-wider"
                        >
                          Status
                        </th>
                        <th
                          scope="col"
                          className="font-bold px-6 py-3 text-left text-sm text-gray-500 tracking-wider"
                        >
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        <td className="px-6 py-2">
                          <input
                            type="text"
                            name="name"
                            placeholder="Filter by Name"
                            value={publishingFilters.name}
                            onChange={handlePublishingFilterChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </td>
                        <td className="px-6 py-2">
                          <input
                            type="text"
                            name="owner"
                            placeholder="Filter by Owner"
                            value={publishingFilters.owner}
                            onChange={handlePublishingFilterChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </td>
                        <td className="px-6 py-2">
                          <select
                            name="instance"
                            value={publishingFilters.instance}
                            onChange={handlePublishingFilterChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          >
                            <option value="">All Instances</option>
                            {publishingUniqueInstances.map(
                              (instance, index) => (
                                <option key={index} value={instance}>
                                  {instance}
                                </option>
                              )
                            )}
                          </select>
                        </td>
                        <td className="px-6 py-2">
                          <select
                            name="status"
                            value={publishingFilters.status}
                            onChange={handlePublishingFilterChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          >
                            <option value="">All Statuses</option>
                            {publishingUniqueStatuses.map((status, index) => (
                              <option key={index} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td></td>
                      </tr>
                      {pagedFilteredPublishingTargets.map((target, index) => (
                        <tr
                          key={target.dashboard_id}
                          className={
                            index % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }
                        >
                          <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                            {target.dashboard_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                            {target.owner}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                            {target.instance_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                target.status === "Published"
                                  ? "bg-green-100 text-green-800"
                                  : target.status === "Pending"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {target.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex flex-wrap gap-1">
                              {target.status === "Unpublished" &&
                                (runAs === "admin" ? (
                                  <>
                                    <button
                                      onClick={() =>
                                        handlePublishClick(target.dashboard_id)
                                      }
                                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                    >
                                      Publish
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() =>
                                        handleOwnerPublishClick(
                                          target.dashboard_id
                                        )
                                      }
                                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                    >
                                      Publish
                                    </button>
                                  </>
                                ))}
                              {target.status === "Pending" &&
                                (runAs === "admin" ? (
                                  <>
                                    <button
                                      onClick={() =>
                                        handlePublishClick(target.dashboard_id)
                                      }
                                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                    >
                                      Publish
                                    </button>
                                    {"\u00A0"}
                                    <button
                                      onClick={() =>
                                        handleUnpublishOrCancel(
                                          target.dashboard_id
                                        )
                                      }
                                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                    >
                                      Reject
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() =>
                                        handleUnpublishOrCancel(
                                          target.dashboard_id
                                        )
                                      }
                                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                                    >
                                      Cancel Publish
                                    </button>
                                  </>
                                ))}
                              {target.status === "Published" && (
                                <button
                                  onClick={() =>
                                    handleUnpublishOrCancel(target.dashboard_id)
                                  }
                                  className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                >
                                  Unpublish
                                </button>
                              )}
                              
                              <button
                                onClick={() =>
                                  handleAddRemoveOwners(target.dashboard_id)
                                }
                                className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                Ownership Admin
                              </button>
                              
                              <button
                                onClick={() =>
                                  handleDashboardRedirectClick(
                                    target.dashboard_id,
                                    target.instance_name,
                                    target.dashboard_name
                                  )
                                }
                                className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                Redirect Dashboard
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="pagination">{renderPublishingPagination()}</div>
              </>
            )}
          </div>
        )}
      </div>
      {showPublishPopup && (
        <TreePopup
          treeData={treeData}
          onClose={() => setShowPublishPopup(false)}
          selectedDashboardId={selectedDashboardId}
        />
      )}
      {showConfirmationPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <p>Are you sure you wish to Unpublish this dashboard?</p>
            <button onClick={() => handleConfirmation(true)}>OK</button>&nbsp;
            <button onClick={() => handleConfirmation(false)}>Cancel</button>
          </div>
        </div>
      )}
      {showRedirectPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <p>
              Redirect of {selectedDashboardType} Dashboard -{" "}
              {selectedDashboardName}
            </p>
            <p>Please select the type of Redirect and the Target</p>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <select
                value={selectedRedirectType}
                onChange={(e) => setSelectedRedirectType(e.target.value)}
              >
                <option value="">Select Redirect Type</option>
                {redirectTypes.map((rt) => (
                  <option key={rt.redirect_type_id} value={rt.redirect_type_id}>
                    {rt.redirect_type}
                  </option>
                ))}
              </select>
            </div>
            <div
              className="redirect-popup-content-info"
              style={{ marginTop: 8, marginBottom: 8 }}
            >
              {redirectTypes.map((rt) => (
                <div key={rt.redirect_type_id}>
                  <b>{rt.redirect_type}</b>: {rt.redirect_description}
                </div>
              ))}
            </div>
            <input
              type="text"
              placeholder="Filter by dashboard name..."
              value={redirectTargetFilter}
              onChange={(e) => setRedirectTargetFilter(e.target.value)}
              style={{ width: "100%", marginBottom: 8 }}
            />
            <div style={{ marginBottom: 8 }}>
              Showing{" "}
              {
                redirectTargets.filter((target) =>
                  target.dashboard_name
                    .toLowerCase()
                    .includes(redirectTargetFilter.toLowerCase())
                ).length
              }{" "}
              dashboards
            </div>
            <div
              style={{
                marginTop: 8,
                marginBottom: 8,
                border: "1px solid #ccc",
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {redirectTargets.length === 0 && (
                <div style={{ padding: 8 }}>Loading...</div>
              )}

              <ul style={{ margin: 0, padding: 0 }}>
                {redirectTargets
                  .filter((target) =>
                    target.dashboard_name
                      .toLowerCase()
                      .includes(redirectTargetFilter.toLowerCase())
                  )
                  .map((target) => (
                    <li
                      key={target.dashboard_id}
                      style={{
                        userSelect: "none",
                        padding: 4,
                        background:
                          selectedRedirectTarget === target.dashboard_id
                            ? "#e0e0e0"
                            : "#f9f9f9",
                        margin: 2,
                      }}
                      onClick={() =>
                        setSelectedRedirectTarget(target.dashboard_id)
                      }
                    >
                      [{target.instance_name}] {target.dashboard_name} (
                      {target.user_full_name})
                    </li>
                  ))}
              </ul>
            </div>
            <button
              onClick={handleRedirectDashboard}
              disabled={!selectedRedirectType || !selectedRedirectTarget}
              style={{
                backgroundColor:
                  !selectedRedirectType || !selectedRedirectTarget
                    ? "#ccc"
                    : undefined,
              }}
            >
              OK
            </button>
            &nbsp;
            <button onClick={() => setShowRedirectPopup(false)}>Cancel</button>
          </div>
        </div>
      )}
      {showCheckboxPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <p>Before publishing please confirm that...</p>
            <div>
              <label>+ The data in this dashboard is correct.</label>
              <input
                type="checkbox"
                name="checkbox1"
                checked={checkboxes.checkbox1}
                onChange={handleCheckboxChange}
              />
            </div>
            <div>
              <label>
                + The dashboard displays contact data of the dashboard owner or
                owner group.
              </label>
              <input
                type="checkbox"
                name="checkbox2"
                checked={checkboxes.checkbox2}
                onChange={handleCheckboxChange}
              />
            </div>
            <div>
              <label>
                + The dashboard data is up to date as of {formatDate()}.
              </label>
              <input
                type="checkbox"
                name="checkbox3"
                checked={checkboxes.checkbox3}
                onChange={handleCheckboxChange}
              />
            </div>
            <button
              disabled={!allCheckboxesChecked}
              onClick={handlePublishDashboard}
            >
              OK
            </button>
            &nbsp;
            <button onClick={handleCloseCheckboxPopup}>Close</button>
          </div>
        </div>
      )}
      {showOwnersPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h4>Dashboard Ownership Admin</h4>
            <div
              style={{ display: "flex", gap: "40px", alignItems: "flex-start" }}
            >
              {/* Owners List */}
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  const userId = e.dataTransfer.getData("userId");
                  const user = nonOwnersList.find(
                    (u) => u.user_id === Number(userId)
                  );
                  if (user) {
                    setOwnersList([...ownersList, user]);
                    setNonOwnersList(
                      nonOwnersList.filter((u) => u.user_id !== user.user_id)
                    );
                  }
                }}
              >
                <label>Dashboard Owners</label>
                <input
                  type="text"
                  placeholder="Search owners..."
                  value={ownerSearch}
                  onChange={(e) => setOwnerSearch(e.target.value)}
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <div
                  style={{
                    minWidth: 200,
                    maxHeight: 300,
                    overflowY: "auto",
                    border: "1px solid #ccc",
                    padding: 0,
                  }}
                >
                  <ul style={{ margin: 0, padding: 0 }}>
                    {filteredOwners.map((user) => (
                      <li
                        key={user.user_id}
                        draggable={user.full_owner !== 1}
                        onDragStart={
                          user.full_owner !== 1
                            ? (e) =>
                                e.dataTransfer.setData("userId", user.user_id)
                            : undefined
                        }
                        style={{
                          userSelect: "none",
                          padding: 4,
                          cursor:
                            user.full_owner !== 1 ? "grab" : "not-allowed",
                          background: "#f9f9f9",
                          margin: 2,
                          opacity: user.full_owner === 1 ? 0.6 : 1,
                        }}
                        onClick={() => setSelectedOwner(user.user_id)}
                        className={
                          selectedOwner === user.user_id ? "selected" : ""
                        }
                        title={
                          user.full_owner === 1
                            ? "This owner cannot be removed"
                            : ""
                        }
                      >
                        {user.user_full_name}
                        {user.full_owner === 1 && " (Full Owner)"}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              {/* Non-Owners List */}
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  const userId = e.dataTransfer.getData("userId");
                  const user = ownersList.find(
                    (u) => u.user_id === Number(userId)
                  );
                  if (user) {
                    setNonOwnersList([...nonOwnersList, user]);
                    setOwnersList(
                      ownersList.filter((u) => u.user_id !== user.user_id)
                    );
                  }
                }}
              >
                <label>Other Users</label>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={nonOwnerSearch}
                  onChange={(e) => setNonOwnerSearch(e.target.value)}
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <div
                  style={{
                    minWidth: 200,
                    maxHeight: 300,
                    overflowY: "auto",
                    border: "1px solid #ccc",
                    padding: 0,
                  }}
                >
                  <ul style={{ margin: 0, padding: 0 }}>
                    {filteredNonOwners.map((user) => (
                      <li
                        key={user.user_id}
                        draggable
                        onDragStart={(e) =>
                          e.dataTransfer.setData("userId", user.user_id)
                        }
                        style={{
                          userSelect: "none",
                          padding: 4,
                          cursor: "grab",
                          background: "#f9f9f9",
                          margin: 2,
                        }}
                        onClick={() => setSelectedNonOwner(user.user_id)}
                        className={
                          selectedNonOwner === user.user_id ? "selected" : ""
                        }
                      >
                        {user.user_full_name}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <button onClick={handleUpdateOwners}>Update Owners</button>
            &nbsp;
            <button onClick={() => setShowOwnersPopup(false)}>Close</button>
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardAdmin;
